import { useEffect } from 'react'
import { Target, TrendingUp, Calendar, DollarSign } from 'lucide-react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { PageHeader } from '@/components/layout'
import { StatCard } from '@/components/shared'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { updateRetirementSettings } from '@/store/slices/retirementSlice'
import { visitPage } from '@/store/slices/gamificationSlice'
import { formatCurrency, formatPercentage } from '@/lib/utils'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

export default function Retirement() {
  const dispatch = useAppDispatch()
  const retirement = useAppSelector(state => state.retirement)

  // Track page visit for explorer achievement
  useEffect(() => {
    dispatch(visitPage('retirement'))
  }, [dispatch])

  const handleSettingChange = (setting: keyof typeof retirement, value: number) => {
    dispatch(updateRetirementSettings({ setting, value }))
  }

  const yearsToRetirement = Math.max(0, retirement.retirementAge - retirement.currentAge)

  const chartData = {
    labels: retirement.chartData.labels,
    datasets: [
      {
        label: 'Portfolio Value',
        data: retirement.chartData.portfolioValues,
        borderColor: 'hsl(var(--chart-2))',
        backgroundColor: 'hsla(var(--chart-2), 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Required Savings',
        data: retirement.chartData.requiredSavings,
        borderColor: 'hsl(var(--chart-5))',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        tension: 0,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          color: 'hsl(var(--muted-foreground))',
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function(context: { dataset: { label?: string }; parsed: { y: number | null } }) {
            return `${context.dataset.label ?? ''}: ${formatCurrency(context.parsed.y ?? 0)}`
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: 'hsl(var(--muted-foreground))', maxTicksLimit: 10 },
      },
      y: {
        grid: { color: 'hsl(var(--border))' },
        ticks: {
          color: 'hsl(var(--muted-foreground))',
          callback: function(value: number | string) {
            return formatCurrency(Number(value))
          },
        },
      },
    },
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Retirement Calculator" 
        description="Plan your retirement and track your progress."
      />

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Success Rate"
          value={formatPercentage(retirement.successRate)}
          icon={Target}
          changeType={retirement.successRate >= 100 ? 'positive' : retirement.successRate >= 80 ? 'neutral' : 'negative'}
          change={retirement.successRate >= 100 ? 'On Track' : 'Needs Work'}
        />
        <StatCard
          title="Years to Retirement"
          value={`${yearsToRetirement} years`}
          icon={Calendar}
        />
        <StatCard
          title="Current Savings"
          value={formatCurrency(retirement.currentSavings)}
          icon={DollarSign}
        />
        <StatCard
          title="Required Savings"
          value={formatCurrency(retirement.requiredSavings)}
          icon={TrendingUp}
        />
      </div>

      {/* Success Rate Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Retirement Readiness</CardTitle>
          <CardDescription>Your progress toward retirement goals</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Success Rate</span>
              <span className={`text-2xl font-bold ${retirement.successRate >= 100 ? 'text-success' : 'text-warning'}`}>
                {formatPercentage(retirement.successRate)}
              </span>
            </div>
            <Progress value={Math.min(100, retirement.successRate)} className="h-3" />
            <p className="text-sm text-muted-foreground">
              {retirement.successRate >= 100
                ? "You're on track to meet your retirement goals!"
                : retirement.successRate >= 80
                ? "You're making good progress, but could use some adjustment."
                : "Consider increasing your savings rate or adjusting your retirement goals."}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Retirement Settings</CardTitle>
            <CardDescription>Adjust your retirement parameters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Current Age</Label>
                <Input
                  type="number"
                  value={retirement.currentAge || ''}
                  onChange={(e) => handleSettingChange('currentAge', parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Retirement Age</Label>
                <Input
                  type="number"
                  value={retirement.retirementAge || ''}
                  onChange={(e) => handleSettingChange('retirementAge', parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Life Expectancy</Label>
                <Input
                  type="number"
                  value={retirement.lifeExpectancy || ''}
                  onChange={(e) => handleSettingChange('lifeExpectancy', parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Current Savings</Label>
                <Input
                  type="number"
                  value={retirement.currentSavings || ''}
                  onChange={(e) => handleSettingChange('currentSavings', parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Annual Contribution</Label>
                <Input
                  type="number"
                  value={retirement.annualContribution || ''}
                  onChange={(e) => handleSettingChange('annualContribution', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Return (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={retirement.expectedReturn || ''}
                  onChange={(e) => handleSettingChange('expectedReturn', parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Desired Annual Income</Label>
                <Input
                  type="number"
                  value={retirement.desiredIncome || ''}
                  onChange={(e) => handleSettingChange('desiredIncome', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Social Security (Annual)</Label>
                <Input
                  type="number"
                  value={retirement.socialSecurityIncome || ''}
                  onChange={(e) => handleSettingChange('socialSecurityIncome', parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Inflation Rate (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={retirement.inflationRate || ''}
                onChange={(e) => handleSettingChange('inflationRate', parseFloat(e.target.value) || 0)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Projection</CardTitle>
            <CardDescription>Projected growth of your retirement savings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[350px]">
              <Line data={chartData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
